import { CSSProperties, FC, ReactNode, useMemo, useState } from "react";
import { RightOutlined } from "@ant-design/icons";
import { clsx } from "clsx";
import { SpendCategory } from "../../types/VendorTypes";
import { categoryLabel } from "./CategoryPath";
import { SearchPopover } from "./SearchPopover";
import styles from "./CategoryPicker.module.less";

/** A taxonomy node with its full path (root first). */
interface CategoryNode {
    label: string;
    path: string[];
    children: CategoryNode[];
}

const toNodes = (categories: SpendCategory[], parent: string[] = []): CategoryNode[] =>
    categories.map((c) => {
        const path = [...parent, c.label];
        return { label: c.label, path, children: toNodes(c.children ?? [], path) };
    });

const leavesOf = (nodes: CategoryNode[]): CategoryNode[] =>
    nodes.flatMap((n) => (n.children.length ? leavesOf(n.children) : [n]));

/** Expansion and selection are keyed by path (labels are unique among siblings only). */
const keyOf = (path: string[]) => path.join("\u0000");

/** The label with the first case-insensitive match of `query` highlighted. */
const highlight = (label: string, query: string) => {
    const i = label.toLowerCase().indexOf(query);
    if (!query || i < 0) return label;
    return (
        <>
            {label.slice(0, i)}
            <mark>{label.slice(i, i + query.length)}</mark>
            {label.slice(i + query.length)}
        </>
    );
};

export interface CategoryTreeProps {
    /** The tree to browse. Only leaves are selectable; branches expand. */
    categories: SpendCategory[];
    /** The selected category, if any. */
    value?: string[];
    onSelect: (path: string[]) => void;
    /** The search: when non-empty, matching leaves are listed flat with their parents. */
    query: string;
}

/** The taxonomy as an expandable tree (or, while searching, the matching leaves). */
export const CategoryTree: FC<CategoryTreeProps> = ({ categories, value, onSelect, query }) => {
    const roots = useMemo(() => toNodes(categories), [categories]);
    const leaves = useMemo(() => leavesOf(roots), [roots]);
    // Every branch starts collapsed.
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const selectedKey = value?.length ? keyOf(value) : undefined;

    const toggle = (node: CategoryNode) => {
        const key = keyOf(node.path);
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const row = (node: CategoryNode, depth: number, q: string) => {
        const branch = node.children.length > 0 && !q;
        const selected = keyOf(node.path) === selectedKey;
        return (
            <div
                className={clsx(styles.row, selected && styles["row--selected"])}
                style={{ "--depth": depth } as CSSProperties}
                onClick={() => (branch ? toggle(node) : onSelect(node.path))}
            >
                <span className={clsx(styles.toggle, !branch && styles["toggle--empty"])}>
                    <RightOutlined />
                </span>
                <span className={styles.name}>{highlight(node.label, q)}</span>
                {q && node.path.length > 1 && (
                    <span className={styles.parents}>
                        {categoryLabel(node.path.slice(0, -1))}
                    </span>
                )}
            </div>
        );
    };

    const tree = (nodes: CategoryNode[], depth: number) =>
        nodes.map((node) => {
            const key = keyOf(node.path);
            const branch = node.children.length > 0;
            const isOpen = expanded.has(key);
            return (
                <li
                    key={key}
                    role="treeitem"
                    aria-selected={key === selectedKey}
                    aria-expanded={branch ? isOpen : undefined}
                >
                    {row(node, depth, "")}
                    {branch && isOpen && <ul role="group">{tree(node.children, depth + 1)}</ul>}
                </li>
            );
        });

    const q = query.trim().toLowerCase();
    const hits = q ? leaves.filter((n) => n.label.toLowerCase().includes(q)) : [];

    return (
        <ul className={styles.tree} role="tree">
            {!q && tree(roots, 0)}
            {q &&
                hits.map((node) => (
                    <li
                        key={keyOf(node.path)}
                        role="treeitem"
                        aria-selected={keyOf(node.path) === selectedKey}
                    >
                        {row(node, 0, q)}
                    </li>
                ))}
            {q && hits.length === 0 && (
                <li className={styles.empty}>No category matches “{query.trim()}”.</li>
            )}
        </ul>
    );
};

export interface CategoryPickerProps {
    /** The tree to browse. Only leaves are selectable; branches expand. */
    categories: SpendCategory[];
    /** The selected category, if any. */
    value?: string[];
    onSelect: (path: string[]) => void;
    /** When given (and a category is selected), a footer row that clears the selection. */
    onClear?: () => void;
    clearLabel?: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** What the field shows; clicking it opens the picker. */
    trigger: ReactNode;
    placeholder?: string;
}

/**
 * A category picker: clicking the trigger opens a popover right below it
 * with a search box on top and the taxonomy tree under it. Branches expand;
 * picking a leaf selects it. Typing lists the matching leaves flat, each
 * with its parents. Escape or a click outside closes it.
 */
export const CategoryPicker: FC<CategoryPickerProps> = ({
    categories,
    value,
    onSelect,
    onClear,
    clearLabel = "Clear",
    open,
    onOpenChange,
    trigger,
    placeholder = "Search categories…",
}) => (
    <SearchPopover
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        placeholder={placeholder}
        footer={onClear && value?.length ? { label: clearLabel, onClick: onClear } : undefined}
    >
        {(query) => (
            <CategoryTree categories={categories} value={value} onSelect={onSelect} query={query} />
        )}
    </SearchPopover>
);
